import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from '../../notification/notification.service';

interface EtsyShopView {
  id: number;
  name: string;
  keystring: string;
  shopId: number | null;
  enabled: boolean;
  connected: boolean;
  hasSharedSecret: boolean;
  tokenExpires: string | null;
  // saját számlázó (Okos Könyvelőm)
  hasOkosKey: boolean;
  okosKeyMasked: string;
  okosCategoryId: string | null;
  okosKeyInput?: string;
  okosCategories?: { id: string; name: string }[];
  // UI-only, nem küldjük vissza:
  sharedSecretInput?: string;
  saving?: boolean;
  updatingEnabled?: boolean;
  fetchingSkus?: boolean;
  importingImages?: boolean;
  missingFromInventory?: string[];
  missingFromMerchant?: string[];
}

@Component({
  selector: 'app-etsy',
  templateUrl: './etsy.component.html',
  styleUrl: './etsy.component.scss'
})
export class EtsyComponent implements OnInit {
  private readonly etsyScopes = [
    'email_r',
    'address_r',
    'profile_r',
    'shops_r',
    'shops_w',
    'transactions_r',
    'transactions_w',
    'listings_r',
    'listings_w'
  ];
  private readonly codeChallenge = 'VaDwxvOxagBcB1lK5UB9ar99WqZidWWzxACphCTbBtg';

  shops: EtsyShopView[] = [];
  loadingShops = false;
  redirectUri: string = location.origin + '/merchants/etsy';

  // Új shop felvétele
  newShopName = '';
  newShopKeystring = '';
  newShopSharedSecret = '';
  creatingShop = false;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadShops();
    this.route.queryParams.subscribe(params => {
      if (params && params['code']?.length) {
        const shopId = Number(params['state']);
        if (shopId) {
          this.getAuthToken(shopId, params['code']);
        }
      }
    });
  }

  loadShops() {
    this.loadingShops = true;
    this.http.get<EtsyShopView[]>('etsy/shops').subscribe({
      next: (res) => {
        this.shops = (res || []).map(shop => ({ ...shop, sharedSecretInput: '', okosKeyInput: '', okosCategories: [] }));
        this.loadingShops = false;
        this.shops.filter(s => s.hasOkosKey).forEach(s => this.loadOkosCategories(s));
      },
      error: () => {
        this.loadingShops = false;
        this.notificationService.show('Etsy shopok betöltése sikertelen!', 'danger');
      }
    });
  }

  addShop() {
    const name = this.newShopName?.trim();
    const keystring = this.newShopKeystring?.trim();
    if (!name) {
      this.notificationService.show('Adj meg egy nevet a shopnak!', 'warning');
      return;
    }
    this.creatingShop = true;
    this.http.post<EtsyShopView>('etsy/shops', {
      name,
      keystring,
      sharedSecret: this.newShopSharedSecret?.trim() || undefined
    }).subscribe({
      next: () => {
        this.creatingShop = false;
        this.newShopName = '';
        this.newShopKeystring = '';
        this.newShopSharedSecret = '';
        this.notificationService.show('Etsy shop létrehozva.', 'success');
        this.loadShops();
      },
      error: (e) => {
        this.creatingShop = false;
        this.notificationService.show(e?.error?.message || 'Shop létrehozása sikertelen!', 'danger');
      }
    });
  }

  saveShop(shop: EtsyShopView) {
    shop.saving = true;
    const payload: any = {
      name: shop.name?.trim(),
      keystring: shop.keystring?.trim(),
      shopId: shop.shopId || null
    };
    if (shop.sharedSecretInput?.trim()) {
      payload.sharedSecret = shop.sharedSecretInput.trim();
    }
    // saját számlázó: a kulcs csak akkor megy, ha újat írtak be
    if (shop.okosKeyInput?.trim()) {
      payload.okosApiKey = shop.okosKeyInput.trim();
    }
    payload.okosCategoryId = shop.okosCategoryId || null;
    this.http.patch<EtsyShopView>(`etsy/shops/${shop.id}`, payload).subscribe({
      next: () => {
        shop.saving = false;
        shop.sharedSecretInput = '';
        this.notificationService.show('Shop mentve.', 'success');
        this.loadShops();
      },
      error: (e) => {
        shop.saving = false;
        this.notificationService.show(e?.error?.message || 'Shop mentése sikertelen!', 'danger');
      }
    });
  }

  loadOkosCategories(shop: EtsyShopView) {
    this.http.get<{ id: string; name: string }[]>(`etsy/shops/${shop.id}/okos-categories`).subscribe({
      next: (cats) => shop.okosCategories = cats || [],
      error: () => shop.okosCategories = [],
    });
  }

  removeOkosKey(shop: EtsyShopView) {
    if (!confirm(`Leveszed a(z) "${shop.name}" saját számlázóját? Ezután a rendelései a cég számlázójával mennek.`)) {
      return;
    }
    this.http.patch<EtsyShopView>(`etsy/shops/${shop.id}`, { okosApiKey: '', okosCategoryId: null }).subscribe({
      next: () => {
        this.notificationService.show('Saját számlázó levéve.', 'success');
        this.loadShops();
      },
      error: (e) => this.notificationService.show(e?.error?.message || 'Nem sikerült.', 'danger'),
    });
  }

  deleteShop(shop: EtsyShopView) {
    if (!confirm(`Biztosan törlöd a(z) "${shop.name}" Etsy shopot? A hozzáférési adatok elvesznek.`)) {
      return;
    }
    this.http.delete(`etsy/shops/${shop.id}`).subscribe({
      next: () => {
        this.notificationService.show('Shop törölve.', 'success');
        this.loadShops();
      },
      error: (e) => {
        this.notificationService.show(e?.error?.message || 'Shop törlése sikertelen!', 'danger');
      }
    });
  }

  toggleEnabled(shop: EtsyShopView, enabled: boolean) {
    shop.updatingEnabled = true;
    this.http.patch<{ enabled: boolean }>(`etsy/shops/${shop.id}/enabled`, { enabled }).subscribe({
      next: (res) => {
        shop.updatingEnabled = false;
        shop.enabled = !!res?.enabled;
      },
      error: (e) => {
        shop.updatingEnabled = false;
        shop.enabled = !enabled;
        this.notificationService.show(e?.error?.message || 'A bekapcsoláshoz jelentkezz be az Etsy-vel!', 'warning');
      }
    });
  }

  connect(shop: EtsyShopView) {
    if (!shop.keystring?.trim()) {
      this.notificationService.show('Előbb mentsd el a Keystringet ehhez a shophoz!', 'warning');
      return;
    }
    window.open(this.getEtsyUrl(shop), '_self');
  }

  private getEtsyUrl(shop: EtsyShopView): string {
    const scope = encodeURIComponent(this.etsyScopes.join(' '));
    const redirectUri = encodeURIComponent(this.redirectUri);
    const clientId = encodeURIComponent(shop.keystring);
    const state = encodeURIComponent(String(shop.id));

    return `https://www.etsy.com/oauth/connect?response_type=code&redirect_uri=${redirectUri}&scope=${scope}&client_id=${clientId}&state=${state}&code_challenge=${this.codeChallenge}&code_challenge_method=S256`;
  }

  private getAuthToken(shopId: number, code: string) {
    this.http.post<{ result: string }>(`etsy/shops/${shopId}/get-token`, {
      code: code,
      redirectUri: this.redirectUri
    }).subscribe({
      next: (res) => {
        if (res.result === 'ok') {
          this.notificationService.show('Etsy hozzáférés sikeres!', 'success');
          this.loadShops();
        }
      },
      error: (err) => {
        console.log(err);
        this.notificationService.show('Etsy hozzáférés sikertelen!', 'danger');
      }
    });
  }

  updateOrders(shop: EtsyShopView) {
    this.http.get(`etsy/shops/${shop.id}/update-orders`).subscribe({
      next: (res) => {
        console.log(res);
        this.notificationService.show('Megrendelések frissítése elindult.', 'success');
      },
      error: () => this.notificationService.show('Megrendelések frissítése sikertelen!', 'danger')
    });
  }

  updateAllOrders() {
    this.http.get('etsy/update-orders').subscribe({
      next: (res) => {
        console.log(res);
        this.notificationService.show('Minden shop frissítése elindult.', 'success');
      },
      error: () => this.notificationService.show('Megrendelések frissítése sikertelen!', 'danger')
    });
  }

  getUnknownSkus(shop: EtsyShopView) {
    shop.fetchingSkus = true;
    this.http.get<{ missingFromInventory: string[]; missingFromMerchant: string[] }>(`etsy/shops/${shop.id}/unknown-skus`).subscribe({
      next: (res) => {
        shop.missingFromInventory = res.missingFromInventory;
        shop.missingFromMerchant = res.missingFromMerchant;
        shop.fetchingSkus = false;
        if (res.missingFromInventory.length === 0 && res.missingFromMerchant.length === 0) {
          this.notificationService.show('Minden SKU rendben van!', 'success');
        }
      },
      error: () => {
        shop.fetchingSkus = false;
        this.notificationService.show('SKU ellenőrzés sikertelen!', 'danger');
      }
    });
  }

  importImages(shop: EtsyShopView) {
    shop.importingImages = true;
    this.http.post<any>(`etsy/shops/${shop.id}/import-images`, {}).subscribe({
      next: (res) => {
        shop.importingImages = false;
        this.notificationService.show(`Kepimport kesz. Uj kepek: ${res?.imported || 0}, kihagyott tobb SKU-s listingek: ${res?.multiSkuSkipped || 0}.`, 'success', 4000);
        console.log('Etsy image import result', res);
      },
      error: (err) => {
        shop.importingImages = false;
        this.notificationService.show(err?.error?.message || 'Etsy kepimport sikertelen!', 'danger');
      }
    });
  }
}
